import { useAnalytics } from 'contexts/Analytics/Analytics';
import { useApi } from 'contexts/Api/Api';
import { getKromaticSiteOriginForHost, resolveAuthApiBaseUrl } from 'helpers';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import AuthModalButton from '../AuthModalButton/AuthModalButton';
import {
    AuthSwitchButton,
    Backdrop,
    CheckboxWrapper,
    CloseButton,
    Container,
    Content,
    Form,
    Loader,
    LoaderCircle,
    ModalDivider,
    Notification,
    OAuthButton,
    OAuthIconWrap,
    PrimaryButton,
    Row,
    Text,
    TextField,
    Title,
} from './AuthModal.styled';

// Shared reset page lives on orca-front so every Kromatic property uses one flow.
// Host-aware base mirrors the repo's staging-vs-prod FE convention
// (getKromaticSiteOriginForHost); unknown/localhost hosts fall back to production.
function buildForgotPasswordUrl(email: string): string {
    const base =
        (typeof window !== 'undefined' && getKromaticSiteOriginForHost(window.location.hostname)) ||
        'https://kromatic.com';
    const trimmedEmail = email.trim();
    return trimmedEmail
        ? `${base}/auth/forgot-password?email=${encodeURIComponent(trimmedEmail)}`
        : `${base}/auth/forgot-password`;
}

function GoogleIcon() {
    return (
        <OAuthIconWrap aria-hidden>
            <svg height="20" viewBox="0 0 18 18" width="20">
                <path
                    d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.2 1.12-.84 2.06-1.8 2.7v2.24h2.92c1.72-1.58 2.68-3.92 2.68-6.58z"
                    fill="#4285F4"
                />
                <path
                    d="M9 18c2.44 0 4.5-.8 6-2.22l-2.92-2.24c-.8.54-1.82.86-3.08.86-2.36 0-4.36-1.58-5.08-3.72H.92v2.3A9 9 0 0 0 9 18z"
                    fill="#34A853"
                />
                <path
                    d="M3.92 10.68A5.4 5.4 0 0 1 3.64 9c0-.58.1-1.14.28-1.68V5.02H.92A9 9 0 0 0 0 9c0 1.44.34 2.8.92 3.98l3-2.3z"
                    fill="#FBBC05"
                />
                <path
                    d="M9 3.58c1.34 0 2.54.46 3.48 1.36l2.6-2.6C13.5.9 11.44 0 9 0A9 9 0 0 0 .92 5.02l3 2.3C4.64 5.18 6.64 3.58 9 3.58z"
                    fill="#EA4335"
                />
            </svg>
        </OAuthIconWrap>
    );
}

export default function AuthModal({ screen = 'login', onClose }: AuthModalProps) {
    const [errors, setErrors] = useState<Record<string, string> | null>(null);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState(false);
    const [loginEmail, setLoginEmail] = useState('');
    const [recoveryEmail, setRecoveryEmail] = useState<string | null>(null);
    const [show, setShow] = useState<AuthModalScreen>(screen);
    const api = useApi();
    const { trackActionEvent } = useAnalytics();
    const isRecoveryScreen = show.startsWith('recovery');
    const shouldShowNotification = notification && isRecoveryScreen;
    const signupMode = screen.startsWith('registration') ? 'join' : 'login';

    useEffect(() => {
        void trackActionEvent('signup_modal_opened', {
            screen: signupMode,
            resource_type: 'tool',
            source: 'auth_modal',
        });
        // Fire once on the hidden→shown transition (modal is mounted only while visible).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClose = useCallback(() => {
        if (!loading) {
            onClose();
        }
    }, [loading, onClose]);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleClose();
            }
        },
        [handleClose]
    );

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    useEffect(() => {
        if (notification) {
            const timeout = setTimeout(() => setNotification(false), 5000);

            return () => {
                clearTimeout(timeout);
            };
        }
    }, [notification]);

    useEffect(() => {
        setErrors(null);
        if (show !== 'recoveryVerify') {
            setRecoveryEmail(null);
        }
        if (!isRecoveryScreen) {
            setNotification(false);
        }
    }, [isRecoveryScreen, show]);

    async function handleRecoveryResend() {
        const response = await request('recoverySend', {
            email: recoveryEmail,
        });

        if (response) {
            setNotification(true);
        }
    }

    async function handleSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();

        if (show === 'login' || show.startsWith('registration')) {
            void trackActionEvent('signup_form_submitted', {
                screen: show.startsWith('registration') ? 'join' : 'login',
                resource_type: 'tool',
                source: 'auth_modal',
            });
        }

        const formData = new FormData(e.currentTarget);
        const values: any = Object.fromEntries(formData.entries());
        if (show.startsWith('registration')) {
            values.email_confirm = values.email_confirm || values.email;
            values.password_confirm = values.password_confirm || values.password;
        }

        const response = await request(show, values);
        if (!response) {
            return;
        }

        if (show === 'recoverySend') {
            setNotification(true);
            setRecoveryEmail(values.email);
            setShow('recoveryVerify');
        } else if (show === 'recoveryVerify') {
            setShow('recoverySuccess');
        }
    }

    async function request(currentScreen: AuthModalScreen, values: any) {
        if (loading) {
            return;
        }

        setErrors(null);
        setLoading(true);

        let response;
        if (currentScreen === 'login') {
            response = await api.login(values);
        } else if (currentScreen === 'recoverySend') {
            response = await api.post('auth/recovery-send', undefined, values);
        } else if (currentScreen === 'recoveryVerify') {
            response = await api.post('auth/recovery-verify', undefined, {
                ...values,
                email: recoveryEmail,
            });
        } else if (currentScreen.startsWith('registration')) {
            response = await api.registration(values);
        }

        setLoading(false);
        if (response?.errors) {
            setErrors(response.errors);
        }
        return !response || response.errors ? null : response;
    }

    function handleGoogleOAuth(source: 'login' | 'registration') {
        if (loading) {
            return;
        }

        setErrors(null);
        const authApiBaseUrl = resolveAuthApiBaseUrl(process.env.NEXT_PUBLIC_AUTH_API_URL, {
            host: window.location.hostname,
            origin: window.location.origin,
        });
        if (!authApiBaseUrl) {
            setErrors({
                detail: 'OAuth is unavailable right now. Please continue with email/password.',
            });
            return;
        }

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `${authApiBaseUrl}/auth/login/google`;
        form.style.display = 'none';

        const addField = (name: string, value: string) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
        };

        const returnUrl = `${window.location.pathname}${window.location.search}` || '/';
        const subscribeToNews =
            source === 'registration'
                ? Boolean((document.getElementById('subscribe_to_news') as HTMLInputElement | null)?.checked)
                : false;

        addField('return_url', returnUrl.startsWith('/') ? returnUrl : '/');
        addField('subscribe_to_news', String(subscribeToNews));

        document.body.appendChild(form);
        form.submit();
    }

    return (
        <Container>
            <Content
                aria-label="Authentication modal"
                aria-modal="true"
                data-testid="auth-modal-container"
                role="dialog"
            >
                <CloseButton
                    aria-label="Close"
                    data-testid="auth-modal-close-button"
                    disabled={loading}
                    onClick={handleClose}
                >
                    <svg aria-hidden fill="currentColor" height="18" viewBox="0 0 24 24" width="18">
                        <path d="M18.3 5.71a.9959.9959 0 0 0-1.41 0L12 10.59 7.11 5.7a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41L10.59 12 5.7 16.89c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0L12 13.41l4.89 4.89c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4z" />
                    </svg>
                </CloseButton>

                {loading && (
                    <Loader aria-hidden role="img" width="40" height="40" viewBox="22 22 44 44">
                        <LoaderCircle cx="44" cy="44" r="20" />
                    </Loader>
                )}

                <Form
                    $loading={loading}
                    aria-label="Authentication form"
                    data-testid="auth-modal-form"
                    onChange={() => setErrors(null)}
                    onSubmit={handleSubmit}
                >
                    {show === 'login' && (
                        <>
                            <Title>Log in</Title>
                            <Row>
                                <OAuthButton
                                    aria-label="Log in with Google"
                                    data-testid="auth-login-google-button"
                                    disabled={loading}
                                    type="button"
                                    onClick={() => handleGoogleOAuth('login')}
                                >
                                    <GoogleIcon />
                                    Log in with Google
                                </OAuthButton>
                            </Row>
                            <ModalDivider>or use your account</ModalDivider>
                            <TextField
                                aria-label="Work email"
                                data-testid="auth-email-input"
                                disabled={loading}
                                name="email"
                                onChange={e => setLoginEmail(e.currentTarget.value)}
                                placeholder="Work email"
                                type="email"
                            />
                            {errors?.email && <Text $error>{errors.email}</Text>}
                            <TextField
                                aria-label="Password"
                                data-testid="auth-password-input"
                                disabled={loading}
                                name="password"
                                placeholder="Password"
                                type="password"
                            />
                            {errors?.password && <Text $error>{errors.password}</Text>}
                            {errors?.detail && (
                                <Text $error data-testid="auth-error-detail">
                                    {errors.detail}
                                </Text>
                            )}
                            <Row>
                                <CheckboxWrapper>
                                    <input
                                        aria-label="Remember me"
                                        disabled={loading}
                                        id="remember_me"
                                        name="remember_me"
                                        type="checkbox"
                                    />
                                    <label htmlFor="remember_me">Remember me</label>
                                </CheckboxWrapper>
                                <AuthSwitchButton
                                    as="a"
                                    aria-label="Forgot your password?"
                                    data-testid="auth-forgot-password-link"
                                    href={buildForgotPasswordUrl(loginEmail)}
                                >
                                    Forgot your password?
                                </AuthSwitchButton>
                            </Row>
                            <Row>
                                <PrimaryButton
                                    $tone="dark"
                                    aria-label="Log in"
                                    data-testid="auth-login-submit-button"
                                    disabled={loading}
                                    type="submit"
                                >
                                    Log in
                                </PrimaryButton>
                            </Row>
                            <ModalDivider />
                            <Row>
                                <Text>Not a member yet?</Text>
                            </Row>
                            <Row>
                                <AuthModalButton
                                    aria-label="Open registration"
                                    data-testid="auth-join-now-button"
                                    disabled={loading}
                                    screen={screen === 'registrationPremium' ? 'registrationPremium' : 'registration'}
                                    onClick={setShow}
                                >
                                    Join now
                                </AuthModalButton>
                            </Row>
                        </>
                    )}

                    {show === 'recoverySend' && (
                        <>
                            <Title>Recover your password</Title>
                            <Text $center $size={16}>
                                Enter your email to receive a verification code.
                            </Text>
                            <TextField
                                aria-label="Work email"
                                data-testid="auth-recovery-email-input"
                                disabled={loading}
                                name="email"
                                placeholder="Work email"
                                type="email"
                            />
                            {errors?.email && <Text $error>{errors.email}</Text>}
                            {errors?.detail && (
                                <Text $error data-testid="auth-error-detail">
                                    {errors.detail}
                                </Text>
                            )}
                            <Row>
                                <PrimaryButton
                                    $tone="yellow"
                                    aria-label="Send code"
                                    data-testid="auth-send-code-button"
                                    disabled={loading}
                                    type="submit"
                                >
                                    Send code
                                </PrimaryButton>
                            </Row>
                            <ModalDivider />
                            <Row>
                                <Text>Remembered your password?</Text>
                            </Row>
                            <Row>
                                <AuthModalButton
                                    aria-label="Back to log in"
                                    data-testid="auth-back-to-login-button"
                                    disabled={loading}
                                    screen="login"
                                    onClick={setShow}
                                >
                                    Log in
                                </AuthModalButton>
                            </Row>
                        </>
                    )}

                    {show === 'recoveryVerify' && (
                        <>
                            <Title>Verify code</Title>
                            <Text $center $size={16}>
                                Enter the code sent to {recoveryEmail}.
                            </Text>
                            <TextField
                                aria-label="Verification code"
                                data-testid="auth-verification-code-input"
                                disabled={loading}
                                name="code"
                                placeholder="Verification code"
                            />
                            {errors?.code && <Text $error>{errors.code}</Text>}
                            <TextField
                                aria-label="New password"
                                data-testid="auth-new-password-input"
                                disabled={loading}
                                name="new_password"
                                placeholder="New password"
                                type="password"
                            />
                            {errors?.new_password && <Text $error>{errors.new_password}</Text>}
                            <TextField
                                aria-label="Confirm new password"
                                data-testid="auth-confirm-password-input"
                                disabled={loading}
                                name="new_password_confirm"
                                placeholder="Confirm new password"
                                type="password"
                            />
                            {errors?.new_password_confirm && <Text $error>{errors.new_password_confirm}</Text>}
                            {errors?.detail && (
                                <Text $error data-testid="auth-error-detail">
                                    {errors.detail}
                                </Text>
                            )}
                            <Row>
                                <PrimaryButton
                                    $tone="yellow"
                                    aria-label="Reset password"
                                    data-testid="auth-reset-password-button"
                                    disabled={loading}
                                    type="submit"
                                >
                                    Reset password
                                </PrimaryButton>
                            </Row>
                            <Row>
                                <AuthSwitchButton
                                    aria-label="Resend code"
                                    data-testid="auth-resend-code-button"
                                    disabled={loading}
                                    type="button"
                                    onClick={handleRecoveryResend}
                                >
                                    Resend code
                                </AuthSwitchButton>
                            </Row>
                        </>
                    )}

                    {show === 'recoverySuccess' && (
                        <>
                            <Title>Password updated</Title>
                            <Text $center $size={16}>
                                Your password has been reset successfully.
                            </Text>
                            <Row>
                                <AuthModalButton disabled={loading} screen="login" onClick={setShow}>
                                    Log in
                                </AuthModalButton>
                            </Row>
                        </>
                    )}

                    {(show === 'registration' || show === 'registrationPremium') && (
                        <>
                            <Title>{show === 'registrationPremium' ? 'Join now' : 'Join for free'}</Title>
                            <Text $center $size={16}>
                                {show === 'registrationPremium'
                                    ? 'Register a premium account'
                                    : 'Please create an account to access Kromatic'}
                            </Text>
                            <Row data-testid="auth-already-have-account">
                                <Text $size={13}>Already have an account?</Text>
                                <AuthModalButton
                                    aria-label="Switch to log in"
                                    data-testid="auth-switch-to-login-button"
                                    disabled={loading}
                                    screen="login"
                                    variant="text"
                                    onClick={setShow}
                                >
                                    Log in
                                </AuthModalButton>
                            </Row>
                            <Row>
                                <OAuthButton
                                    aria-label="Sign up with Google"
                                    data-testid="auth-registration-google-button"
                                    disabled={loading}
                                    type="button"
                                    onClick={() => handleGoogleOAuth('registration')}
                                >
                                    <GoogleIcon />
                                    Sign up with Google
                                </OAuthButton>
                            </Row>
                            <ModalDivider>or use your account</ModalDivider>
                            <TextField
                                aria-label="Your name"
                                data-testid="auth-registration-name-input"
                                disabled={loading}
                                name="name"
                                placeholder="Your name"
                            />
                            {errors?.name && <Text $error>{errors.name}</Text>}
                            <TextField
                                aria-label="Work email"
                                data-testid="auth-registration-email-input"
                                disabled={loading}
                                name="email"
                                placeholder="Work email"
                                type="email"
                            />
                            {errors?.email && <Text $error>{errors.email}</Text>}
                            <TextField
                                aria-label="Confirm email"
                                data-testid="auth-registration-email-confirm-input"
                                disabled={loading}
                                name="email_confirm"
                                placeholder="Confirm email"
                                type="email"
                            />
                            {errors?.email_confirm && <Text $error>{errors.email_confirm}</Text>}
                            <TextField
                                aria-label="Password (at least 8 characters)"
                                data-testid="auth-registration-password-input"
                                disabled={loading}
                                name="password"
                                placeholder="Password (at least 8 characters)"
                                type="password"
                            />
                            {errors?.password && <Text $error>{errors.password}</Text>}
                            <TextField
                                aria-label="Confirm password"
                                data-testid="auth-registration-password-confirm-input"
                                disabled={loading}
                                name="password_confirm"
                                placeholder="Confirm password"
                                type="password"
                            />
                            {errors?.password_confirm && <Text $error>{errors.password_confirm}</Text>}
                            {errors?.detail && (
                                <Text $error data-testid="auth-error-detail">
                                    {errors.detail}
                                </Text>
                            )}
                            <Row>
                                <CheckboxWrapper>
                                    <input
                                        aria-label="Subscribe to Kromatic Newsletters"
                                        defaultChecked
                                        disabled={loading}
                                        id="subscribe_to_news"
                                        name="subscribe_to_news"
                                        type="checkbox"
                                    />
                                    <label htmlFor="subscribe_to_news">Subscribe to Kromatic Newsletters</label>
                                </CheckboxWrapper>
                            </Row>
                            <Row>
                                <PrimaryButton
                                    $tone="yellow"
                                    aria-label="Join now"
                                    data-testid="auth-registration-submit-button"
                                    disabled={loading}
                                    type="submit"
                                >
                                    Join now
                                </PrimaryButton>
                            </Row>
                        </>
                    )}
                </Form>

                {shouldShowNotification && (
                    <Notification $show={shouldShowNotification} data-testid="auth-recovery-notification">
                        Check your email
                    </Notification>
                )}
            </Content>
            <Backdrop onClick={handleClose} />
        </Container>
    );
}
